import React, { useEffect, useRef, useState } from "react";
import {
  FiChevronDown,
  FiTrash2,
} from "react-icons/fi";
import "flag-icons/css/flag-icons.min.css";

const stopText = (value) => String(value ?? "").trim();

const prettyDate = (value) => {
  const raw = stopText(value);

  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }

  const [year, month, day] = raw.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return raw;
  }

  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(date);
};

const COUNTRY_CODES = {
  portugal: "PT",
  espanha: "ES",
  espana: "ES",
  spain: "ES",
  franca: "FR",
  france: "FR",
  alemanha: "DE",
  germany: "DE",
  italia: "IT",
  italy: "IT",
  austria: "AT",
  belgica: "BE",
  belgium: "BE",
  paisesbaixos: "NL",
  netherlands: "NL",
  holanda: "NL",
  polonia: "PL",
  poland: "PL",
  suica: "CH",
  switzerland: "CH",
  luxemburgo: "LU",
  luxembourg: "LU",
  dinamarca: "DK",
  denmark: "DK",
  suecia: "SE",
  sweden: "SE",
  noruega: "NO",
  norway: "NO",
  finlandia: "FI",
  finland: "FI",
  irlanda: "IE",
  ireland: "IE",
  reinounido: "GB",
  unitedkingdom: "GB",
  chequia: "CZ",
  czechia: "CZ",
  eslovaquia: "SK",
  slovakia: "SK",
  eslovenia: "SI",
  slovenia: "SI",
  hungria: "HU",
  hungary: "HU",
  romenia: "RO",
  romania: "RO",
  croacia: "HR",
  croatia: "HR",
};

const getStopCountry = (value) => {
  const raw = stopText(value);

  if (!raw) {
    return { label: "", code: "" };
  }

  const normalized = raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

  const code = /^[A-Za-z]{2}$/.test(raw)
    ? raw.toUpperCase()
    : COUNTRY_CODES[normalized] || "";

  let label = raw;

  if (code && /^[A-Za-z]{2}$/.test(raw)) {
    try {
      label = new Intl.DisplayNames(["en"], {
        type: "region",
      }).of(code) || raw;
    } catch {
      label = raw;
    }
  }

  return { label, code };
};

export default function CreateLoadStopCard({
  kind,
  index,
  stop = {},
  initiallyExpanded = false,
  importRevision = 0,
  openRequest = 0,
  invalid = false,
  canDelete = false,
  onDelete,
  onExpand,
  onOperationMinutesChange,
  children,
}) {
  const [expanded, setExpanded] = useState(
    Boolean(initiallyExpanded)
  );

  const lastImportRevision = useRef(importRevision);
  const lastOpenRequest = useRef(openRequest);
  const expandedDetailsRequested = useRef(false);

  useEffect(() => {
    if (
      importRevision &&
      importRevision !== lastImportRevision.current
    ) {
      expandedDetailsRequested.current = false;
      setExpanded(false);
    }

    lastImportRevision.current = importRevision;
  }, [importRevision]);

  useEffect(() => {
    if (
      openRequest &&
      openRequest !== lastOpenRequest.current
    ) {
      setExpanded(true);
    }

    lastOpenRequest.current = openRequest;
  }, [openRequest]);

  useEffect(() => {
    if (invalid) {
      setExpanded(true);
    }
  }, [invalid]);

  useEffect(() => {
    if (!expanded || expandedDetailsRequested.current) {
      return;
    }

    expandedDetailsRequested.current = true;
    onExpand?.();
  }, [expanded, onExpand]);

  const reviewRequired = Boolean(
    stop?.aiGeocode?.requiresReview === true ||
    stop?.aiPlaceResolution?.requiresReview === true ||
    stop?.aiLocationResolution?.requiresReview === true
  );

  const verified =
    Boolean(stopText(stop.coordinates)) &&
    !reviewRequired &&
    [
      stop?.aiGeocode?.status,
      stop?.aiPlaceResolution?.status,
      stop?.aiLocationResolution?.status,
    ].some(
      (status) =>
        stopText(status).toUpperCase() === "MATCH"
    );

  const fallbackName = `${
    kind === "pickup" ? "Pickup" : "Delivery"
  } ${index + 1}`;

  const locationName = stopText(stop.location);
  const city = stopText(stop.city);
  const postalCode = stopText(stop.postalCode);

  const name =
    locationName ||
    city ||
    fallbackName;

  const country = getStopCountry(stop.country);
  const date = prettyDate(stop.date);
  const hour = stopText(stop.hour);
  const reference = stopText(stop.reference);


  const needsAttention = invalid || reviewRequired;

  const panelId = `loadevo-v6-${kind}-body-${index}`;

  return (
    <article
      className={
        `loadevo-v6-stop-card` +
        `${expanded ? " is-expanded" : ""}` +
        `${needsAttention ? " is-review" : ""}`
      }
    >
      <div className="loadevo-v6-stop-heading">
        <button
          type="button"
          className="loadevo-v6-stop-toggle"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() =>
            setExpanded((previous) => !previous)
          }
        >
          <span className="loadevo-v6-stop-number">
            {index + 1}
          </span>

          <span className="loadevo-v6-stop-identity">
            <strong title={name}>
              {name}
            </strong>

            {(city || postalCode || country.code) && (
              <small>
                {(country.code || city || postalCode) && (
                  <span className="loadevo-v6-stop-location-inline">
                    {country.code && (
                      <span
                        className={`fi fi-${country.code.toLowerCase()} loadevo-v6-stop-flag`}
                        title={country.label}
                        aria-hidden="true"
                      />
                    )}

                    {city && city !== name && (
                      <span
                        className="loadevo-v6-stop-city"
                        title={city}
                      >
                        {city}
                      </span>
                    )}

                    {postalCode && (
                      <span
                        className="loadevo-v6-stop-postcode"
                        title={postalCode}
                      >
                        {postalCode}
                      </span>
                    )}
                  </span>
                )}

              </small>
            )}
          </span>

          {(date || hour) && (
            <span className="loadevo-v6-stop-date">
              {date && (
                <strong>{date}</strong>
              )}

              {hour && (
                <small>{hour}</small>
              )}
            </span>
          )}

          {reference && (
            <span
              className="loadevo-v6-stop-reference"
              title={reference}
            >
              {reference}
            </span>
          )}


          <span
            className={
              `loadevo-v6-stop-status` +
              `${
                needsAttention
                  ? " is-review"
                  : verified
                  ? " is-verified"
                  : ""
              }`
            }
          >
            {needsAttention
              ? "Review"
              : verified
              ? "Ready"
              : "Pending"}
          </span>

          <FiChevronDown
            className="loadevo-v6-stop-chevron"
            aria-hidden="true"
          />
        </button>

        {canDelete && (
          <button
            type="button"
            className="loadevo-v6-stop-remove"
            title={
              kind === "pickup"
                ? "Remove pickup"
                : "Remove delivery"
            }
            aria-label={`Remove ${kind} ${index + 1}`}
            onClick={onDelete}
          >
            <FiTrash2 aria-hidden="true" />
          </button>
        )}
      </div>

      <div
        id={panelId}
        className="loadevo-v6-stop-body"
        aria-hidden={!expanded}
      >
        <div className="loadevo-v6-stop-body-inner">
          <div className="cl-stop-operation">
            <div className="cl-stop-operation-copy">
              <strong>
                {kind === "pickup"
                  ? "Estimated loading time"
                  : "Estimated unloading time"}
              </strong>
              <small>
                {kind === "pickup"
                  ? "Default: 60 min"
                  : "Default: 45 min"}
              </small>
            </div>

            <div className="cl-stop-operation-control">
              <input
                type="number"
                min="0"
                max="720"
                step="5"
                aria-label={
                  kind === "pickup"
                    ? "Estimated loading minutes"
                    : "Estimated unloading minutes"
                }
                value={stop.operationMinutes ?? ""}
                placeholder={
                  kind === "pickup" ? "60" : "45"
                }
                onChange={(event) =>
                  onOperationMinutesChange?.(
                    event.target.value
                  )
                }
              />
              <span>min</span>
            </div>
          </div>

          {children}
        </div>
      </div>
    </article>
  );
}